import React, { useRef, useEffect } from 'react'
import dayjs from 'dayjs'
import {
  Modal,
  TextBody,
  TextHeading,
  Box,
  Link,
  Button,
  useModal,
  useFocus,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import { Account as AfgAccount } from '@ally/data-types-afg-accounts'
import { maskAccountNumber } from '@ally/aos-utils'

import { trackHomeLoanModal } from '../../analytics'
import { useHostServices } from '../../HostServices'
import { StyledMortgageButton } from '../../Shared/AccountDetailsLink'

export type ModalTriggerProps = {
  openModal?: () => void
}

export const DELINQUENT_MORTGAGE_ARIA_LABEL =
  'Select to learn why your mortgage account needs attention.'

const StyledModalHeading = styled(TextHeading)(
  ({ theme }): string => `
    padding-bottom: ${theme.space.lg}
    `,
)

export const NeedsAttentionActionModalTrigger: React.FC<ModalTriggerProps> = ({
  openModal,
}) => {
  return (
    <Button
      text="Needs Attention"
      variant="link"
      onClick={openModal}
      ariaLabel={DELINQUENT_MORTGAGE_ARIA_LABEL}
    />
  )
}

export const AccountNameModalTrigger: React.FC<
  ModalTriggerProps & {
    account: AfgAccount
    focusRef?: React.RefObject<HTMLButtonElement>
  }
> = ({ openModal, account, focusRef }) => {
  // istanbul ignore next
  const name = account.nickName || account.name
  const modalAriaLabel = `${name} account ending in ${maskAccountNumber(
    account.accountNumber,
    'none',
  )}`
  return (
    <StyledMortgageButton
      onClick={openModal}
      data-allytmln={name}
      aria-label={DELINQUENT_MORTGAGE_ARIA_LABEL.concat(` ${modalAriaLabel}`)}
      ref={focusRef}
    >
      {name}
    </StyledMortgageButton>
  )
}

export const DelinquentMortgageModal = ({
  trigger,
}: {
  trigger: React.FunctionComponentElement<any>
}): React.ReactElement => {
  const headingRef = useRef<HTMLHeadingElement>({} as HTMLHeadingElement)
  useEffect(() => {
    trackHomeLoanModal('needs attention')
  }, [])
  return (
    <Modal
      ref={headingRef}
      trigger={trigger}
      content={
        <Box p="xxl">
          <StyledModalHeading size="md" ref={headingRef} tabIndex={-1}>
            Your account needs attention.
          </StyledModalHeading>
          <TextBody tag="p" size="sm">
            You can&apos;t make online payments, and we&apos;ve restricted your
            access to statements and tax forms.
          </TextBody>
          <TextBody tag="p" size="sm">
            Call{' '}
            <Link
              to="tel:18552562559"
              text="1-855-256-2559"
              withAllyTM="mortgage delinquent phone number in modal"
              aria-label="1, 8 5 5, 2 5 6, 2 5 5 9"
            />{' '}
            to discuss this account.
          </TextBody>
        </Box>
      }
    />
  )
}

const paidInFullText = 'paid in full'

const getHeaderText = (statusReason: string): string => {
  switch (statusReason) {
    case 'PAID_IN_FULL':
      return paidInFullText
    case 'TRANSFERRED':
    case 'CLAIMS_CLOSE_OUT':
    case 'FORECLOSED':
      return 'transferred'
    // istanbul ignore next
    default:
      // istanbul ignore next
      return paidInFullText
  }
}

const PaidOffContent: React.FC<{
  account: AfgAccount
  headingRef: React.MutableRefObject<HTMLHeadingElement>
  getDetailsFocusFunction: () => void
}> = ({ account, headingRef, getDetailsFocusFunction }) => {
  const { redirectToMortgage } = useHostServices()
  const { closeModal } = useModal()
  // istanbul ignore next
  const statusReason = account.meta?.statusReason ?? ''

  const currentYear = new Date().getFullYear()
  const DATE = dayjs(new Date(`December 31, ${currentYear + 1}`)).format(
    'MMM DD, YYYY',
  )

  const headerText = getHeaderText(statusReason)

  useEffect(() => {
    trackHomeLoanModal(headerText)
  }, [headerText])

  return (
    <Box p="xxl">
      <StyledModalHeading size="md" ref={headingRef} tabIndex={-1}>
        Your account has been {headerText}.
      </StyledModalHeading>
      <TextBody tag="p" size="sm">
        You&apos;ll be able to view your statements and tax forms until {DATE}.
      </TextBody>
      <Button
        variant="link"
        text="Get more details"
        onClick={(): void => {
          closeModal()
          redirectToMortgage(account.accountNumber, {
            onCancel: () => {
              setTimeout(getDetailsFocusFunction, 0)
            },
          })
        }}
        marginTop="xl"
        data-testid="mortgage-modal-paid-off-get-more-details-button"
        withAllyTM="homeLoans-accountDoesntExist-moreDetails"
      />
    </Box>
  )
}

const PaidOffButtonTrigger: React.FC<
  ModalTriggerProps & {
    /** Used for returning focus to the action button if they decide to open the 2nd modal
     * by clicking 'Get more details' and then cancel out of that modal.
     */
    focusRef: React.RefObject<HTMLButtonElement>
  }
> = ({ openModal, focusRef }) => {
  return (
    <Button
      ref={focusRef}
      variant="link"
      text="Get Details"
      onClick={openModal}
    />
  )
}

export const PaidOffModal = ({
  account,
  trigger,
}: {
  account: AfgAccount
  trigger?: (ref: React.RefObject<HTMLButtonElement>) => JSX.Element
}): React.ReactElement => {
  const headingRef = useRef<HTMLHeadingElement>({} as HTMLHeadingElement)
  const [focusRef, getDetailsFocusFunction] = useFocus<HTMLButtonElement>()

  return (
    <Modal
      ref={headingRef}
      trigger={
        trigger?.(focusRef) ?? <PaidOffButtonTrigger focusRef={focusRef} />
      }
      content={
        <PaidOffContent
          account={account}
          headingRef={headingRef}
          getDetailsFocusFunction={getDetailsFocusFunction}
        />
      }
    />
  )
}
